import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { OraclePrompt } from '@modules/oracle/store/types';
import { mapChatResponseToOraclePromptByID } from '@modules/oracle/utils';
import {
  Chat,
  EqlQueryPropertiesViewFragmentDoc,
  GetChatByTokenInput,
  Query,
} from 'generated/graphql';
import { useEffect, useRef, useState } from 'react';
import { useGetUser } from 'v2/core/services/user';

const CitationAnalysisFragment = gql`
  fragment CitationAnalysisView on Analysis {
    viewToken
    useCase
    title
    context {
      eqlExpression
    }
  }
`;

const AssistantResponseAnswerFields = gql`
  ${CitationAnalysisFragment}
  fragment AssistantResponseAnswerFields on AssistantResponseAnswer {
    tokensV2 {
      eql {
        eql {
          eql
          properties {
            ...EqlQueryPropertiesView
          }
        }
      }
      id
      artefact {
        analysis {
          ...CitationAnalysisView
        }
        artefactID
        feedbackCount
        forClaim
      }
      text {
        id
        markdownContent
        summaryDetails {
          examples {
            count
            id
            claimExample {
              forClaim
              records {
                labelID
                recordID
              }
            }
            labelExample {
              recordIDs
              forLabel {
                createdAt
                description
                displayName
                id
                isVisible
                type
                updateInProgress
              }
            }
          }
          label {
            details {
              createdAt
              description
              displayName
              id
              isVisible
              type
              updateInProgress
            }
          }
        }
      }
    }
    userQuote {
      endTime
      exampleID
      externalLink
      labelID
      quote
      recordID
      source
      sourceIcon
      startTime
    }
  }
  ${EqlQueryPropertiesViewFragmentDoc}
`;

const AssistantResponseStepAnswerFields = gql`
  ${CitationAnalysisFragment}
  fragment AssistantResponseStepAnswerFields on AssistantResponseStepAnswer {
    end
    isSameAsAnswer
    stepID
    tokensV2 {
      eql {
        eql {
          eql
          properties {
            aggregatePeriod
            duration {
              endBefore
              relative {
                unit
                value
              }
              startAfter
            }
            formula {
              description
              displayName
              eqlFunction
              group
              helpCentreURL
              isSupported
              shortName
            }
            groupBy {
              entity
              entitySentiment
              labelID
              language
              metadata {
                keys
              }
              predictedOn
              reason
              recordSentiment
              source
            }
            isCoOccurring
            type
            whereClause
          }
        }
      }
      id
      artefact {
        analysis {
          ...CitationAnalysisView
        }
        artefactID
        feedbackCount
        forClaim
      }
      text {
        id
        markdownContent
        summaryDetails {
          examples {
            count
            id
            claimExample {
              forClaim
              records {
                labelID
                recordID
              }
            }
            labelExample {
              recordIDs
              forLabel {
                createdAt
                description
                displayName
                id
                isVisible
                type
                updateInProgress
              }
            }
          }
          label {
            details {
              createdAt
              description
              displayName
              id
              isVisible
              type
              updateInProgress
            }
          }
        }
      }
    }
    userQuotes {
      endTime
      exampleID
      externalLink
      labelID
      quote
      recordID
      source
      sourceIcon
      startTime
    }
  }
`;

const FETCH_CHAT_BY_TOKEN = gql`
  ${AssistantResponseAnswerFields}
  ${AssistantResponseStepAnswerFields}
  query getChatByToken($tenantID: ID!, $userID: ID!, $token: ID!) {
    getChatByToken(
      request: { tenantID: $tenantID, userID: $userID, token: $token }
    ) {
      chat {
        id
        viewToken
        editToken
        sessionID
        title
        createdBy {
          id
          firstName
          lastName
        }
        createdAt
        updatedAt
        status
        responses {
          id
          question
          answer {
            ...AssistantResponseAnswerFields
          }
          steps {
            id
            question
            answer {
              ...AssistantResponseStepAnswerFields
            }
          }
          createdAt
          previousResponseID
          nextResponseIDs
          isEdited
          editedFromResponseID
        }
      }
    }
  }
`;

interface useFetchChatByTokenProps {
  token?: string;
  skip?: boolean;
  onCompleted?: (data: Query) => void;
}

export const useFetchChatByToken = ({
  token,
  skip = false,
  onCompleted,
}: useFetchChatByTokenProps) => {
  // Used state to avoid apollo cache / redundant fetches
  const [chat, setChat] = useState<Chat | undefined>(undefined);
  const [promptByID, setPromptByID] = useState<
    Record<string, OraclePrompt> | undefined
  >(undefined);
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();
  const { showErrorNotification } = useNotificationContext();

  const [fetchChatByToken, { loading }] = useLazyQuery<
    Query,
    GetChatByTokenInput
  >(FETCH_CHAT_BY_TOKEN, {
    fetchPolicy: 'network-only',
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to fetch this chat',
        props: {},
      });
    },
    onCompleted,
  });

  const dependencies = {
    userID,
    tenantID,
  };
  const dependenciesRef = useRef(dependencies);
  dependenciesRef.current = dependencies;

  useEffect(() => {
    const fetchChat = async () => {
      if (skip || !token) {
        return;
      }

      const resp = await fetchChatByToken({
        variables: {
          tenantID: dependenciesRef.current.tenantID,
          userID: dependenciesRef.current.userID,
          token: token,
        },
      });

      const chatData = resp?.data?.getChatByToken?.chat;
      setChat(chatData);
      setPromptByID(
        chatData ? mapChatResponseToOraclePromptByID(chatData) : undefined,
      );
    };

    fetchChat();
  }, [fetchChatByToken, skip, token]);

  return {
    isLoading: loading,
    promptByID,
    chat,
    currentPromptID:
      chat?.responses[chat?.responses.length - 1]?.id ?? undefined,
  };
};
